import {
  Component,
  OnInit,
  AfterViewInit,
  CUSTOM_ELEMENTS_SCHEMA,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Location } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { createEmbeddingContext } from "amazon-quicksight-embedding-sdk";
import { DashboardService } from "../../services/admin/dashboard.service";
import { FrameOptions } from "amazon-quicksight-embedding-sdk";
import { QuicksightService } from "../../services/user/quicksight.service";
import { CloudWatchRumService } from "../../services/cloudwatch-rum.service";
import { firstValueFrom } from "rxjs";

@Component({
  selector: "app-embedded-dashboard",
  standalone: true,
  imports: [CommonModule],
  templateUrl: "./embedded-dashboard.component.html",
  styleUrls: ["./embedded-dashboard.css"],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class EmbeddedDashboardComponent implements OnInit, AfterViewInit {
  private isViewInitialized = false;
  private frameMountStartTime: number | null = null;
  debugInfo: string | null = null;

  dashboardId: string = "";
  sheetId?: string = "";
  embedUrl: string = "";
  contentTitle: string = "";
  contentType: string = "";
  contentId: string = "";
  description: string = "";
  descriptionObj: object = {};

  items = [
    { name: "Home", url: "/" },
    { name: "Reports and Dashboards", url: "" },
    { name: "", url: "../" },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dashboardService: DashboardService,
    private location: Location,
    private quicksightService: QuicksightService,
    private cloudWatchRumService: CloudWatchRumService,
  ) {}

  ngOnInit(): void {
    // Get dashboard ID from route parameters
    this.dashboardId = this.route.snapshot.paramMap.get("dashboardId") || "";
    
    // Get any query parameters that might override backend values
    const queryTitle = this.route.snapshot.queryParamMap.get("title");

    this.route.queryParams.subscribe((params) => {
      // Update breadcrumb for pinned content
      if (params['origin'] === 'pinned') {
        this.items[1].name = `Pinned ${this.items[1].name}`;
        this.items[1].url = '/home/favorited-reports-dashboards';
      }
      
      // The title will be set from backend response, but can be overridden by query params
      if (queryTitle) {
        this.contentTitle = queryTitle;
        this.updateBreadcrumbTitle();
      }
    });

    this.getEmbeddedUrl();
    
  }

  ngAfterViewInit(): void {
    this.isViewInitialized = true;
    if (this.embedUrl) {
      this.embedDashboard();
    }
  }

  onBreadcrumbSelected(event: Event) {
    const payload = (
      event as CustomEvent<{
        name: string;
        url: string;
      }>
    ).detail;

    if (payload.url === '' || payload.url === '../') {
      this.location.back();
      return;
    }

    this.router.navigate([payload.url]);
  }

  updateBreadcrumbTitle() {
    this.items[2].name = this.contentTitle;
  }

  getEmbeddedUrl() {
    this.dashboardService.getEmbeddedUrl(this.dashboardId, this.contentId).subscribe({
      next: (response: { embed_url: string; dashboard_id?: string; content_id?: string; content_type?: string; title?: string; sheet_id?: string }) => {
        this.embedUrl = response.embed_url;
        
        // Set values from backend response
        if (response.content_id) {
          this.contentId = response.content_id;
        }
        if (response.content_type) {
          this.contentType = response.content_type;
        }
        if (response.title && !this.contentTitle) {
          this.contentTitle = response.title;
          this.updateBreadcrumbTitle();
        }
        if (response.sheet_id) {
          this.sheetId = response.sheet_id;
        }
        
        if (this.isViewInitialized) {
          this.embedDashboard();
        }
      },
      error: (error: any) => {
        console.error("Error fetching embedded URL:", error);
        // Record CloudWatch RUM custom event for report render failure
        this.cloudWatchRumService.addCustomEvent('report_render_failed', {
          reportId: this.contentId,
          dashboardId: this.dashboardId,
          errorType: 'embed_url_fetch_failed',
          errorMessage: error?.error?.message || error?.message || 'Unknown error',
          contentType: this.contentType,
        });
      },
    });
  }

  async embedDashboard() {
    if (!this.embedUrl) {
      console.error("embedUrl is not available.");
      return;
    }

    // Ensure we have the latest tokens before embedding
    this.quicksightService.refreshTokens();
    
    const frameOptions: FrameOptions = {
      url: this.embedUrl,
      container: "#dashboard-container",
      height: "100%",
      width: "100%",
      className: "embedded-dashboard-frame",
      onChange: (changeEvent: { eventName: string }) => {
        switch (changeEvent.eventName) {
          case "FRAME_MOUNTED":
            this.frameMountStartTime = performance.now();
            window.scrollTo(0, 0);
            break;
          case "FRAME_LOADED":
            // Calculate embedding duration if we have a start time
            if (this.frameMountStartTime !== null) {
              const frameLoadEndTime = performance.now();
              const embeddingDuration = frameLoadEndTime - this.frameMountStartTime;
              
              // Record CloudWatch RUM custom event for successful embedding
              this.cloudWatchRumService.addCustomEvent('report_embedding_loaded', {
                dashboardId: this.dashboardId,
                contentId: this.contentId,
                contentType: this.contentType,
                contentTitle: this.contentTitle,
                sheetId: this.sheetId || null,
                embeddingDurationMs: embeddingDuration,
              });
              
              // Reset the start time
              this.frameMountStartTime = null;
            }
            break;
        }
      },
    };

    // Define a basic content options object
    const contentOptions = {
      locale: "en-US",
      toolbarOptions: {
        export: this.contentType === 'Dashboard',
        undoRedo: false,
        reset: true,
      },
      sheetOptions: {
        singleSheet: !!this.sheetId?.trim() && this.sheetId?.trim().toLowerCase() !== "null",
      },
      parameterPassingMethod: 'URL'
    };

    try {
      // Ensure the user is registered with QuickSight
      await firstValueFrom(this.quicksightService.ensureRegistered());
      
      // Get client ID with retry logic to ensure we have a valid value
      let clientId = await this.getClientIdWithRetry();
      
      if (!clientId) {
        console.warn("Could not obtain a valid client ID after multiple attempts. Proceeding with original options.");
        this.embedWithOriginalOptions(frameOptions, contentOptions);
        return;
      }
      
      console.log("Successfully obtained client ID:", clientId);
      
      // Add client ID to URL parameters if available
      if (frameOptions.url) {
        const url = new URL(frameOptions.url);
        url.searchParams.append('clientId', clientId);
        frameOptions.url = url.toString();
      }

      const enhancedContentOptions = JSON.parse(JSON.stringify(contentOptions))

      // iterate through all properties on this.descriptionObj and take all non null / non empty string values and add those properties and values to a parameters property on enhancedContenOptions obj
      if (!enhancedContentOptions.parameters) {
        enhancedContentOptions.parameters = [];
      }
      
      for (const [key, value] of Object.entries(this.descriptionObj)) {
        if (value !== null && value !== undefined && value !== '') {
          enhancedContentOptions.parameters.push({
            Name: key,
            Values: value,
          });
        }
      }
      
      // Add the pClientId parameter to the content options
      const updatedContentOptions = await firstValueFrom(
        this.quicksightService.addClientIdToEmbedConfig(enhancedContentOptions)
      );

      console.log('enhancedContentOptions', enhancedContentOptions);
      console.log('updatedContentOptions', updatedContentOptions);
      
      // Verify the content options include the client ID
      if (!this.verifyClientIdInOptions(updatedContentOptions, clientId)) {
        console.warn("Client ID not properly added to content options. Using original embedding method.");
        this.embedWithOriginalOptions(frameOptions, enhancedContentOptions);
        return;
      }
      
      // Embed the dashboard
      const embeddingContext: any = await createEmbeddingContext();
      embeddingContext.embedDashboard(frameOptions, updatedContentOptions);
    } catch (error) {
      console.error('Error during dashboard embedding or registration:', error);
      // Record CloudWatch RUM custom event for report render failure
      this.cloudWatchRumService.addCustomEvent('report_render_failed', {
        reportId: this.contentId,
        dashboardId: this.dashboardId,
        errorType: 'embedding_failed',
        errorMessage: error instanceof Error ? error.message : String(error),
        contentType: this.contentType,
      });
      // If registration fails, still try to embed with original options
      this.embedWithOriginalOptions(frameOptions, contentOptions);
    }
  }

  /**
   * Fallback method to embed the dashboard with the original options
   * Used when there's an error adding the client ID
   */
  private async embedWithOriginalOptions(frameOptions: FrameOptions, contentOptions: any) {
    try {
      console.log("Embedding dashboard with original options");
      const embeddingContext: any = await createEmbeddingContext();
      embeddingContext.embedDashboard(frameOptions, contentOptions);
    } catch (error) {
      console.error('Error during fallback dashboard embedding:', error);
      // Record CloudWatch RUM custom event for report render failure
      this.cloudWatchRumService.addCustomEvent('report_render_failed', {
        reportId: this.contentId,
        dashboardId: this.dashboardId,
        errorType: 'fallback_embedding_failed',
        errorMessage: error instanceof Error ? error.message : String(error),
        contentType: this.contentType,
      });
    }
  }

  /**
   * Attempts to get a valid client ID with retry logic
   * @returns A Promise that resolves to the client ID or null if not available after retries
   */
  private async getClientIdWithRetry(maxRetries = 3, delayMs = 500): Promise<string | null> {
    let retries = 0;
    
    while (retries < maxRetries) {
      // Try to get the client ID
      const clientId = this.quicksightService.getClientCode();
      
      if (clientId) {
        return clientId; // Success!
      }
      
      console.log(`Attempt ${retries + 1}/${maxRetries}: Client ID not available yet. Retrying in ${delayMs}ms...`);
      
      // Wait before trying again
      await new Promise(resolve => setTimeout(resolve, delayMs));
      
      // Force a token refresh on subsequent attempts
      if (retries > 0) {
        this.quicksightService.refreshTokens();
      }
      
      retries++;
    }
    
    console.warn(`Failed to get client ID after ${maxRetries} attempts.`);
    return null;
  }
  
  /**
   * Verifies that the client ID is properly included in the content options
   * @param options The content options to verify
   * @param clientId The expected client ID
   * @returns true if the client ID is properly included, false otherwise
   */
  private verifyClientIdInOptions(options: any, clientId: string): boolean {
    if (!options || !options.parameters) {
      return false;
    }
    
    // Look for the pClientId parameter
    const clientIdParam = options.parameters.find((param: any) => param.Name === 'pClientId');
    
    if (!clientIdParam || !clientIdParam.Values || !clientIdParam.Values.length) {
      return false;
    }
    
    // Check that the value matches our client ID
    return clientIdParam.Values[0] === clientId;
  }
}
